import React, { useState, useEffect } from "react";
import SpaContactModal from "../../SpaContactModal";
import { Heart, Mountain, Leaf, MapPin, Clock } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";

interface AlpineAirTherapyModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const AlpineAirTherapyModal: React.FC<AlpineAirTherapyModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [isSpaModalOpen, setIsSpaModalOpen] = useState(false);
  const { t } = useLanguage();

  // Track modal open
  useEffect(() => {
    if (isOpen && typeof window !== "undefined") {
      window.dataLayer = window.dataLayer || [];

      // Clear all tracking variables first (prevents data pollution)
      // NOTE: button_text, button_location, and modal_name are preserved to retain context
      window.dataLayer.push({
        // Clear modal variables (will be reset below)
        modal_type: undefined,
        modal_name: undefined,
        parent_modal: undefined,
        page_location: undefined,
        // Preserve button variables (retain context of triggering action)
        // button_text: undefined,     // ← Keep this!
        // button_location: undefined, // ← Keep this!
        // Clear booking variables
        room_type: undefined,
        // Clear contact action variables
        contact_type: undefined,
        contact_source: undefined,
        event_category: undefined,
        event_label: undefined,
        phone_number: undefined,
        email_address: undefined,
        whatsapp_number: undefined,
        region: undefined,
        destination: undefined,
        booking_method: undefined,
        // Clear event consultation variables
        event_type: undefined,
        consultation_method: undefined,
        service_category: undefined,
        // Clear Calendly variables
        calendly_action: undefined,
        calendly_location: undefined,
      });

      // Now push the actual modal open event with clean data
      window.dataLayer.push({
        event: "modal_open",
        modal_type: "spa_treatment",
        modal_name: "alpine_air_therapy",
        page_location: window.location.pathname,
      });
    }
  }, [isOpen]);

  return (
    <div
      className={`fixed inset-0 z-[100] flex items-center justify-center ${isOpen ? "block" : "hidden"}`}
      data-oid="q0xns2e"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
        data-oid="zic:7di"
      />

      {/* Modal Content */}
      <div
        className="relative bg-elegant-white rounded-xl shadow-2xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto"
        data-oid="t06xcjz"
      >
        {/* Header */}
        <div
          className="sticky top-0 bg-elegant-white border-b border-gray-200 px-6 py-4 rounded-t-xl"
          data-oid="v6shatm"
        >
          <div className="flex items-center justify-between" data-oid="0czc.9v">
            <h2
              className="text-2xl md:text-3xl font-playfair text-gray-900"
              data-oid="e2.x64e"
            >
              {t("spa.alpineAirTherapy.title")}
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              aria-label="Close modal"
              data-oid="r644vy3"
            >
              <svg
                className="w-6 h-6 text-gray-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                data-oid="e74jzft"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                  data-oid="xg3ovde"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6" data-oid="jyq_dw:">
          <div className="mb-6" data-oid="qo5w7rf">
            <p className="text-lg text-gray-900 mb-6" data-oid="a8e02.q">
              {t("spa.alpineAirTherapy.description")}
            </p>
          </div>

          {/* Facilities Grid */}
          <div
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8"
            data-oid="lan7yas"
          >
            {/* Relaxation Areas */}
            <div
              className="bg-pebble-beige rounded-lg p-6 shadow-lg"
              data-oid="8oayiba"
            >
              <div className="flex items-center mb-4" data-oid="v6vszbi">
                <Heart
                  className="w-8 h-8 text-alpine-gold mr-3"
                  data-oid="0b63y93"
                />

                <h3
                  className="text-xl font-playfair font-semibold text-gray-900"
                  data-oid="8vg:car"
                >
                  {t("spa.alpineAirTherapy.relaxationAreas")}
                </h3>
              </div>
              <ul
                className="space-y-2 text-gray-900 text-sm"
                data-oid="n:8waot"
              >
                <li data-oid="xwglxng">
                  • {t("spa.alpineAirTherapy.facilities.panoramicLounge")}
                </li>
                <li data-oid="8y:t4p7">
                  • {t("spa.alpineAirTherapy.facilities.alpineGarden")}
                </li>
                <li data-oid="kc63dv2">
                  • {t("spa.alpineAirTherapy.facilities.swissSauna")}
                </li>
                <li data-oid="inw83_2">
                  • {t("spa.alpineAirTherapy.facilities.steamRoom")}
                </li>
                <li data-oid="7z1ps-f">
                  • {t("spa.alpineAirTherapy.facilities.infraredSauna")}
                </li>
                <li data-oid="qm73jy8">
                  • {t("spa.alpineAirTherapy.facilities.meditationCorner")}
                </li>
              </ul>
            </div>

            {/* Core Facilities */}
            <div
              className="bg-pebble-beige rounded-lg p-6 shadow-lg"
              data-oid="dufxoej"
            >
              <div className="flex items-center mb-4" data-oid="d2ct-ve">
                <Mountain
                  className="w-8 h-8 text-alpine-gold mr-3"
                  data-oid="qx7qx94"
                />

                <h3
                  className="text-xl font-playfair font-semibold text-gray-900"
                  data-oid="-5cz41w"
                >
                  {t("spa.alpineAirTherapy.coreFacilities")}
                </h3>
              </div>
              <ul
                className="space-y-2 text-gray-900 text-sm"
                data-oid="bqla.xv"
              >
                <li data-oid=":d4vkvw">
                  • {t("spa.alpineAirTherapy.facilities.bioSpa")}
                </li>
                <li data-oid="hpvs0gy">
                  • {t("spa.alpineAirTherapy.facilities.steamRoom")}
                </li>
                <li data-oid="3:odew3">
                  • {t("spa.alpineAirTherapy.facilities.fitnessCenter")}
                </li>
                <li data-oid=".s98rhj">
                  • {t("spa.alpineAirTherapy.facilities.yogaPavilion")}
                </li>
                <li data-oid="24ipklq">
                  • {t("spa.alpineAirTherapy.facilities.wellnessTerrace")}
                </li>
                <li data-oid="w8ctdp6">
                  • {t("spa.alpineAirTherapy.facilities.hydrotherapyPools")}
                </li>
                <li data-oid="2v2x1-.">
                  • {t("spa.alpineAirTherapy.facilities.massageRooms")}
                </li>
                <li data-oid="avyrr28">
                  • {t("spa.alpineAirTherapy.facilities.winterGarden")}
                </li>
              </ul>
            </div>

            {/* Unique Features */}
            <div
              className="bg-pebble-beige rounded-lg p-6 shadow-lg md:col-span-2 lg:col-span-1"
              data-oid="p02j6-p"
            >
              <div className="flex items-center mb-4" data-oid="1jnl898">
                <Leaf
                  className="w-8 h-8 text-alpine-gold mr-3"
                  data-oid="p1efdj."
                />

                <h3
                  className="text-xl font-playfair font-semibold text-gray-900"
                  data-oid=":nzo8ar"
                >
                  {t("spa.alpineAirTherapy.uniqueFeatures")}
                </h3>
              </div>
              <ul
                className="space-y-2 text-gray-900 text-sm"
                data-oid="jrh4so4"
              >
                <li data-oid="3-4aibv">
                  • {t("spa.alpineAirTherapy.facilities.mountainSpringWater")}
                </li>
                <li data-oid="m9a:t28">
                  • {t("spa.alpineAirTherapy.facilities.alpineBotanicals")}
                </li>
                <li data-oid="6thhnqb">
                  • {t("spa.alpineAirTherapy.facilities.swissDesign")}
                </li>
                <li data-oid="4n7ih4-">
                  • {t("spa.alpineAirTherapy.facilities.peptideTherapies")}
                </li>
                <li data-oid="c6l0wa7">
                  • {t("spa.alpineAirTherapy.facilities.healingPractices")}
                </li>
                <li data-oid="cja45tr">
                  • {t("spa.alpineAirTherapy.facilities.panoramicViews")}
                </li>
              </ul>
            </div>
          </div>

          {/* Alpine Air Therapy Benefits */}
          <div
            className="bg-gradient-to-r from-pebble-beige to-elegant-white rounded-lg p-6 mb-6"
            data-oid="hyq4ja5"
          >
            <h3
              className="text-xl font-playfair font-semibold text-gray-900 mb-4"
              data-oid="1d13qep"
            >
              {t("spa.alpineAirTherapy.benefitsTitle")}
            </h3>
            <div className="grid md:grid-cols-2 gap-4" data-oid="v1olmjx">
              <div className="flex items-start" data-oid="ssar:pt">
                <MapPin
                  className="w-5 h-5 text-alpine-gold mr-2 mt-1 flex-shrink-0"
                  data-oid="md1wugz"
                />

                <div data-oid="rmjta7u">
                  <h4
                    className="font-semibold text-gray-900 mb-1"
                    data-oid="06a9yqx"
                  >
                    {t("spa.alpineAirTherapy.pureMountainAir")}
                  </h4>
                  <p className="text-gray-900 text-sm" data-oid="o2_ohfc">
                    {t("spa.alpineAirTherapy.pureMountainAirDesc")}
                  </p>
                </div>
              </div>
              <div className="flex items-start" data-oid="gjdfo.p">
                <Clock
                  className="w-5 h-5 text-alpine-gold mr-2 mt-1 flex-shrink-0"
                  data-oid="p7z8fku"
                />

                <div data-oid="6.9f4d3">
                  <h4
                    className="font-semibold text-gray-900 mb-1"
                    data-oid="9jipl8r"
                  >
                    {t("spa.alpineAirTherapy.seasonalTherapy")}
                  </h4>
                  <p className="text-gray-900 text-sm" data-oid="extplya">
                    {t("spa.alpineAirTherapy.seasonalTherapyDesc")}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Concierge Booking */}
          <div
            className="bg-alpine-gold/10 border border-alpine-gold/20 rounded-lg p-6 text-center"
            data-oid="dturveb"
          >
            <h4
              className="text-lg font-playfair font-semibold text-gray-900 mb-2"
              data-oid="0:.x37b"
            >
              {t("spa.alpineAirTherapy.readyToExperience")}
            </h4>
            <p className="text-gray-900 mb-4" data-oid="eq.b44_">
              {t("spa.alpineAirTherapy.contactConciergeDesc")}
            </p>
            <Button
              onClick={() => setIsSpaModalOpen(true)}
              trackingType="contact_modal"
              trackingLabel="Contact for Spa Booking"
              trackingLocation="treatment_modal"
              trackingData={{
                modalType: "spa",
                parentModal: "alpine_air_therapy",
              }}
              className="bg-alpine-gold hover:bg-alpine-gold/90 text-white px-6 py-3 text-base font-semibold rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
              data-oid="h88p7:l"
            >
              {t("spa.alpineAirTherapy.contactConciergeButton")}
            </Button>
          </div>
        </div>
      </div>

      {/* Spa Contact Modal */}
      <SpaContactModal
        isOpen={isSpaModalOpen}
        onClose={() => setIsSpaModalOpen(false)}
        parentModal="alpine_air_therapy"
        data-oid="z1sxhiz"
      />
    </div>
  );
};

export default AlpineAirTherapyModal;
